/**
 * Minimal RefineVoice Widget - Bare bones functionality
 *
 * This widget provides voice recording capabilities without any styling.
 * It can be used to enhance any existing textarea or div element.
 *
 * Features:
 * - Voice recording and transcription
 * - Word-level alternatives and confidence
 * - No built-in UI (just functionality)
 * - Works with any existing text input element
 */

import { useRecording } from '@refinevoice/core';
import { useEffect, useRef } from 'react';

interface MinimalWidgetProps {
  /** Reference to the target element (textarea, div, etc.) */
  targetRef: React.RefObject<HTMLElement>;

  /** Callback when transcript changes */
  onTranscriptChange?: (text: string) => void;

  /** Callback when recording state changes */
  onRecordingStateChange?: (isRecording: boolean) => void;

  /** Widget key for authentication */
  widgetKey?: string;

  /** Backend URL. Empty string (the default) means same origin. */
  backendUrl?: string;

  /** Language code */
  language?: string;
}

export function MinimalWidget({
  targetRef,
  onTranscriptChange,
  onRecordingStateChange,
  widgetKey,
  backendUrl = '',
  language = 'en',
}: MinimalWidgetProps) {
  const elementRef = targetRef;
  const lastContentRef = useRef<string>('');

  // Use core recording functionality
  const {
    transcript,
    interimTranscript,
    isRecording,
    isConnecting,
    isProcessing,
    startRecording,
    stopRecording,
  } = useRecording({
    widgetKey: widgetKey || 'wk_free_demo',
    backendUrl,
    language,
    onError: (error) => console.error('MinimalWidget recording error:', error),
  });

  // Update target element with transcript
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const fullText = transcript + (interimTranscript ? ' ' + interimTranscript : '');

    // Only update if content actually changed
    if (fullText !== lastContentRef.current) {
      lastContentRef.current = fullText;

      // Handle different element types
      if (element instanceof HTMLTextAreaElement || element instanceof HTMLInputElement) {
        element.value = fullText;
        // Trigger React's onChange if needed
        const event = new Event('input', { bubbles: true });
        element.dispatchEvent(event);
      } else {
        // For div or other contenteditable elements
        element.textContent = fullText;
      }

      onTranscriptChange?.(fullText);
    }
  }, [transcript, interimTranscript, onTranscriptChange, elementRef]);

  // Notify parent of recording state changes
  useEffect(() => {
    onRecordingStateChange?.(isRecording);
  }, [isRecording, onRecordingStateChange]);

  // Expose control methods on the element
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Add control methods to the element
    (element as any).startVoiceRecording = startRecording;
    (element as any).stopVoiceRecording = stopRecording;
    (element as any).isVoiceRecording = isRecording;
    (element as any).isVoiceConnecting = isConnecting;
    (element as any).isVoiceProcessing = isProcessing;

    return () => {
      // Clean up methods
      (element as any).startVoiceRecording = undefined;
      (element as any).stopVoiceRecording = undefined;
      (element as any).isVoiceRecording = undefined;
      (element as any).isVoiceConnecting = undefined;
      (element as any).isVoiceProcessing = undefined;
    };
  }, [elementRef, startRecording, stopRecording, isRecording, isConnecting, isProcessing]);

  // This component doesn't render anything - it's purely functional
  return null;
}

/**
 * Hook to use the minimal widget functionality
 *
 * Example usage:
 * ```tsx
 * const textareaRef = useRef<HTMLTextAreaElement>(null);
 *
 * useMinimalWidget({
 *   targetRef: textareaRef,
 *   onTranscriptChange: (text) => console.log(text),
 *   widgetKey: 'your-key'
 * });
 *
 * return <textarea ref={textareaRef} placeholder="Speak or type..." />;
 * ```
 */
export function useMinimalWidget(props: MinimalWidgetProps) {
  // The component handles everything, we just need to render it
  return <MinimalWidget {...props} />;
}
